// Full-screen shoppable video feed.
// Clicking a card's [data-video-open] trigger opens the element's <dialog> with a vertical,
// snap-scrolling feed of every filled card in the row (each keeps its product tag and add-to-cart).
// The feed loops endlessly: it holds three identical sets and, once scrolling settles in the
// first or last set, silently jumps one set to the matching slide in the middle set.
// The video in view plays; the rest pause. A sound toggle unmutes the feed. Like toggles persist
// per product in localStorage; share uses the native share sheet or copies the product link.
const LIKE_STORAGE_KEY = 'shoppable-video-likes';

class VideoFeed extends HTMLElement {
  #controller = null;
  #observer = null;
  #muted = true;
  #setSize = 0;
  #settleTimer = 0;
  #toastTimer = 0;
  #jumping = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.dialog = this.querySelector('[data-video-feed-dialog]');
    this.track = this.dialog?.querySelector('[data-video-feed-track]');
    this.soundButton = this.dialog?.querySelector('[data-video-feed-sound]');
    this.toast = this.dialog?.querySelector('[data-video-feed-toast]');
    if (!this.dialog || !this.track) return;

    this.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('[data-video-open]');
        if (!trigger || this.dialog.contains(trigger)) return;
        const index = this.#cards().indexOf(trigger.closest('.shoppable-video'));
        this.#open(Math.max(0, index));
      },
      { signal }
    );

    this.soundButton?.addEventListener('click', () => this.#setMuted(!this.#muted), { signal });
    this.dialog.addEventListener('close', () => this.#teardown(), { signal });
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) {
          this.dialog.close();
          return;
        }
        const like = event.target.closest('[data-video-like]');
        if (like) {
          this.#toggleLike(like);
          return;
        }
        const share = event.target.closest('[data-video-share]');
        if (share) this.#share(share);
      },
      { signal }
    );

    // Loop recentering once scrolling settles (scrollend where supported, debounced scroll always).
    const settle = () => {
      clearTimeout(this.#settleTimer);
      this.#settleTimer = setTimeout(() => this.#recenter(), 120);
    };
    this.track.addEventListener('scroll', settle, { signal, passive: true });
    this.track.addEventListener('scrollend', () => this.#recenter(), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#toastTimer);
  }

  #cards() {
    return Array.from(this.querySelectorAll('[data-video-row] .shoppable-video:not(.shoppable-video--empty)'));
  }

  #jumpTo(slide) {
    if (!slide) return;
    this.#jumping = true;
    this.track.style.scrollSnapType = 'none';
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollTop = slide.offsetTop;
    requestAnimationFrame(() => {
      this.track.style.scrollSnapType = '';
      this.track.style.scrollBehavior = '';
      this.#jumping = false;
    });
  }

  #recenter() {
    const count = this.#setSize;
    if (this.#jumping || !this.dialog.open || count < 2) return;
    const slides = this.track.children;
    const slideHeight = slides[0]?.offsetHeight || 1;
    const index = Math.round(this.track.scrollTop / slideHeight);
    let target = index;
    if (index < count) target = index + count;
    else if (index >= count * 2) target = index - count;
    if (target !== index) this.#jumpTo(slides[target]);
  }

  #likes() {
    try {
      return JSON.parse(localStorage.getItem(LIKE_STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  #syncLikes() {
    const liked = new Set(this.#likes());
    this.track.querySelectorAll('[data-video-like]').forEach((button) => {
      button.setAttribute('aria-pressed', String(liked.has(button.dataset.likeKey)));
    });
  }

  #toggleLike(button) {
    const key = button.dataset.likeKey;
    if (!key) return;
    const liked = new Set(this.#likes());
    if (liked.has(key)) liked.delete(key);
    else liked.add(key);
    try {
      localStorage.setItem(LIKE_STORAGE_KEY, JSON.stringify([...liked]));
    } catch {}
    // Update every copy of this product's button (the loop holds three).
    this.#syncLikes();
  }

  async #share(button) {
    const url = button.dataset.shareUrl;
    const title = button.dataset.shareTitle;
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {}
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      this.#showToast();
    } catch {}
  }

  #showToast() {
    if (!this.toast) return;
    this.toast.textContent = this.toast.dataset.message || '';
    this.toast.dataset.visible = 'true';
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => {
      this.toast.dataset.visible = 'false';
    }, 1800);
  }

  #open(index) {
    const cards = this.#cards();
    this.#setSize = cards.length;
    const loopCards = cards.length > 1 ? [...cards, ...cards, ...cards] : cards;

    this.track.replaceChildren();
    for (const card of loopCards) {
      const slide = document.createElement('div');
      slide.className = 'video-feed__slide';
      const copy = card.cloneNode(true);
      copy.querySelectorAll('[data-video-open]').forEach((el) => el.remove());
      copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      for (const el of [copy, ...copy.querySelectorAll('*')]) {
        for (const attr of Array.from(el.attributes)) {
          if (attr.name.startsWith('data-shopify')) el.removeAttribute(attr.name);
        }
      }
      copy.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.removeAttribute('aria-hidden');
        video.muted = this.#muted;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'metadata';
      });
      slide.append(copy);
      this.track.append(slide);
    }
    this.#syncLikes();

    // Pause the row previews while the feed is open.
    cards.forEach((card) => card.querySelector('video')?.pause());

    this.dialog.showModal();
    const startIndex = cards.length > 1 ? index + cards.length : index;
    this.#jumpTo(this.track.children[startIndex]);
    this.track.focus({ preventScroll: true });

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target.querySelector('video');
          if (!video) continue;
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        }
      },
      { root: this.track, threshold: 0.6 }
    );
    Array.from(this.track.children).forEach((slide) => this.#observer.observe(slide));
  }

  #setMuted(muted) {
    this.#muted = muted;
    this.track.querySelectorAll('video').forEach((video) => {
      video.muted = muted;
    });
    if (this.soundButton) {
      this.soundButton.setAttribute('aria-pressed', String(!muted));
      this.soundButton.dataset.state = muted ? 'off' : 'on';
    }
  }

  #teardown() {
    this.#observer?.disconnect();
    this.track.querySelectorAll('video').forEach((video) => video.pause());
    this.track.replaceChildren();
    this.#cards().forEach((card) => card.querySelector('video')?.play().catch(() => {}));
  }
}

if (!customElements.get('s-video-feed')) customElements.define('s-video-feed', VideoFeed);
